import { includes, isEmpty } from 'lodash';
import { isMobile } from 'react-device-detect';
import validator from 'validator';
import { isNewSystem } from '../../switch';
import { isPassageDeviceType } from '../devices/helper';
import { ALERT_OPTIONS } from './constants';

// Get a concat of user name & last name from its ID
export const getUserFromId = (users, id) => users.find(user => user.id === id);

export const getUsersFromIds = (users, selectedUsersId) => {
    const selectedUsers = [];
    selectedUsersId?.forEach(id => {
        selectedUsers.push(getUserFromId(users, id));
    });
    return selectedUsers;
};

export const getUsersByGroupId = (users, groups, id) => {
    const usersByGroups = {};
    groups?.forEach(grp => {
        usersByGroups[grp.group_id] = [];
    });
    users?.forEach(user => {
        user.group_memberships?.forEach(group_membership => {
            if (usersByGroups[group_membership.group.group_id]) {
                usersByGroups[group_membership.group.group_id].push(user);
            }
        });
    });
    return usersByGroups[id];
};

// Add the 'name' to user object for UserAvatar to work
export const getUserName = (user, withoutCompany = false) => {
    if (!user) return undefined;
    const displayCompanyName = user.workspace_role?.workspace_role_name === 'Contractor' && !withoutCompany && !isEmpty(user.custom_field?.company);

    if (user.first_name === undefined) {
        if (displayCompanyName) { return `${user.custom_field?.company} - ${user.invite_email}`; }
        return user.invite_email;
    }

    if (user?.username === 'qrcodes@merciyanis.com') {
        return 'Un utilisateur anonyme';
    }
    if (displayCompanyName) {
        return `${user.custom_field?.company} - ${user.first_name || ''} ${user.last_name || ''}`;
    }

    return `${user.first_name || ''} ${user.last_name || ''}`;
};

export const addNametoUserObject = user => {
    const newUser = { ...user };
    if (user?.first_name && user.last_name) {
        newUser.name = getUserName(newUser, true);
    } else {
        newUser.name = undefined;
    }
    return newUser;
};

export const getFirstLetterUserName = user => {
    const first_name_letter = user.first_name.charAt(0);
    const last_name_letter = user.last_name.charAt(0);
    return `${first_name_letter} ${last_name_letter}`;
};

export const getUserGroupIds = user => {
    const groups = [];
    user.group_memberships?.forEach(membership => { groups.push(membership.group.group_id); });
    return groups;
};

export const getUserGroupIdsByGroupType = (user, groupType) => {
    const groups = [];
    user?.group_memberships?.forEach(membership => {
        if (membership?.group?.custom_field?.type === groupType) {
            groups.push(membership.group.group_id);
        }
    });
    return groups;
};

export const isEmail = user => user?.email && user.email?.length > 0 && validator.isEmail(user.email.trim()); // TODO : rename this function to hasEmail and will be removed when all the code is updated

export const hasEmail = user => user?.email && user.email?.length > 0 && validator.isEmail(user.email.trim());

export const isUserWithoutMail = currentUser => !isEmpty(currentUser) && !isEmail(currentUser);

export const isContractorWithoutMail = currentUser => isUserWithoutMail(currentUser) && currentUser.workspace_role?.workspace_role_name === 'Contractor';

export const isContractorWithMail = currentUser => !isUserWithoutMail(currentUser) && currentUser.workspace_role?.workspace_role_name === 'Contractor';

export const isOwnerOrAdmin = user => user && (user.workspace_role?.workspace_role_name === 'Owner' || user.workspace_role?.workspace_role_name === 'Admin');

export const canSeeMobileRoundView = currentUser => currentUser.workspace_role?.workspace_role_name === 'Owner'
    || currentUser.workspace_role?.workspace_role_name === 'Admin'
    || currentUser.workspace_role?.workspace_role_name === 'Contractor';

export const canSeeMobileTicketView = (currentUser, workspaceSettings) => currentUser.workspace_role?.workspace_role_name === 'Owner'
    || currentUser.workspace_role?.workspace_role_name === 'Admin'
    || isContractorWithMail(currentUser)
    || isContractorWithoutMail(currentUser) && !!workspaceSettings?.['show_tickets_view_for_users_without_email_on_mobile']
    || currentUser.workspace_role?.workspace_role_name === 'User';

// Check if a user has access to the device view (same rights on mobile and pc)
export const canSeeDeviceView = (currentUser, hide_contractor_device_view) => currentUser.workspace_role?.workspace_role_name === 'Owner'
    || currentUser.workspace_role?.workspace_role_name === 'Admin' || currentUser.workspace_role?.workspace_role_name === 'Contractor' && (hide_contractor_device_view !== undefined && !hide_contractor_device_view);

/**
 * This function is used to check which buttons to display on the footbar on mobile
 * @param currentUser {object} Current connected user information
 * @param currentPath {string} Current path that user is navigation to
 * @param devices {[device]} List of the devices on the WS
 * @param workspaceSettings {object} All of the workspace settings
 * @return {array} If current user can see the bottom navigation bar on a mobile device
 */
export const visibleMobileFootbarButtons = (currentUser, currentPath, devices, workspaceSettings) => {
    let displayedButtons = [];
    const hasPassageDeviceInWorkspace = devices.some(device => isPassageDeviceType(device));
    const isAllowedRoute = currentPath.includes('/tickets') || currentPath.includes('/rounds') || currentPath.includes('/devices');
    const isOwner = currentUser.workspace_role?.workspace_role_name === 'Owner';
    const isAdmin = currentUser.workspace_role?.workspace_role_name === 'Admin';
    const hasRoundGroupMembership = currentUser?.group_memberships?.some(group => group?.group?.group_name === 'Rondes');

    // Cannot see the footbar
    if (!isMobile || !isAllowedRoute) {
        return [];
    }

    // Rights to see the round button
    if (canSeeMobileRoundView(currentUser)) {
        if (isOwner || isAdmin) {
            if (hasPassageDeviceInWorkspace) {
                displayedButtons.push('rounds');
            }
        } else if (isContractorWithMail(currentUser)) {
            // Only contractors with the right skill can see the round button
            if (hasPassageDeviceInWorkspace && hasRoundGroupMembership) {
                displayedButtons.push('rounds');
            }
        } else if (isContractorWithoutMail(currentUser)) {
            // The default view for contractors without mail is the round view
            // hasPassageDeviceInWorkspace is not required since no matter the devices on the workspace they must at least see the round view
            // because they do not necessarily see the tickets view
            displayedButtons.push('rounds');
        }
    }
    // Rights to see the device button
    if (canSeeDeviceView(currentUser, workspaceSettings.hide_contractor_device_view)) { displayedButtons.push('devices'); }

    // Rights to see the ticket button
    if (canSeeMobileTicketView(currentUser, workspaceSettings)) { displayedButtons.push('tickets'); }

    // We do not display the footbar if there is less than 2 buttons
    if (displayedButtons.length < 2) { displayedButtons = []; }

    return displayedButtons;
};

/**
 * This function is used to check if current user can see the bottom navigation bar on a mobile device
 * @param currentUser {object} Current connected user information
 * @param currentPath {string} Current path that user is navigation to
 * @param devices {[device]} List of the devices on the WS
 * @param workspaceSettings {object} All of the workspace settings
 * @return {boolean} If current user can see the bottom navigation bar on a mobile device
 */
export const canSeeMobileBottomNavigation = (currentUser, currentPath, devices, workspaceSettings) => {
    return visibleMobileFootbarButtons(currentUser, currentPath, devices, workspaceSettings).length > 0;
};

export const userRoleCanSeeCalendarView = showUserCalendarView => showUserCalendarView === true;
export const contractorRoleCanSeeCalendarView = showContractorCalendarView => showContractorCalendarView === true;

export const isUserOrContractorWithCalendarView = (currentUserRole, workspaceSettings) => currentUserRole === 'User' && userRoleCanSeeCalendarView(workspaceSettings?.show_user_calendar_view)
    || currentUserRole === 'Contractor' && contractorRoleCanSeeCalendarView(workspaceSettings?.show_contractor_calendar_view);

export const canCreateUpdateDeleteInstance = userRole => userRole === 'Admin' || userRole === 'Owner';

export const getVisibleUsers = (users, currentUser = null) => {
    let result = users.filter(user => user?.username !== 'merciyanis@iot-rocket.space'
        && user?.username !== 'samfm@merciyanis.com' && user?.username !== 'servicenow@merciyanis.com'
        && user?.username !== 'teams@merciyanis.com');
    if (currentUser?.username !== 'merciyanis@iot-rocket.space') {
        result = result.filter(user => user?.username !== 'messaging@merciyanis.com'  && user?.username !== 'qrcodes@merciyanis.com');
    }
    return result;
};


export const getUsersOptionFromIds = (users, usersIdsList) => {
    const usersOptions = [];
    usersIdsList?.forEach(userId => {
        const user = getUserFromId(users, userId);
        usersOptions.push({ value: getUserName(user, true), id: user.id });
    });
    return usersOptions;
};

export const getUsersOptionFromUser = users => {
    const result = [];
    if (users) {
        users?.forEach(user => {
            result.push({ id: user.id, value: user.invite_email ? `✉️ ${getUserName(user)}` : getUserName(user) });
        });
        return result;
    }
    return result;
};

export const getUserNameFromEmail = (users, email) => {
    if (email) {
        const user = users.find(obj => obj?.username === email);
        if (user) return getUserName(user);
    }
    return '';
};

export const getUserGroupIdsByType = (user, groups, excludeType) => {
    const result = [];
    if (!isEmpty(excludeType)) {
        user.group_memberships?.forEach(membership => { result.push(membership.group.group_id); });
    }
    groups?.forEach(group => {
        if (group?.custom_field && !includes(excludeType, group.custom_field?.type)) result.push(group.group_id);
    });
    return result;
};

export const getUserGroupsByGroupType = (user, groupType) => {
    const groups = [];
    user?.group_memberships?.forEach(membership => {
        if (membership?.group?.custom_field?.type === groupType) {
            groups.push(membership.group);
        }
    });
    return groups;
};

export const requiredRoleToSeeRoundView = (userWorkspaceRole, workspaceSettings) => userWorkspaceRole === 'Admin' || userWorkspaceRole === 'Owner'
    || isUserOrContractorWithCalendarView(userWorkspaceRole, workspaceSettings);

export const getWorkspaceRoleNameFromId = (workspaceRoles, workspaceRoleId) => workspaceRoles.find(workspaceRole => workspaceRole.workspace_role_id === workspaceRoleId)?.workspace_role_name;

/**
 * Get available alerts for the current user based on the user role / workspace settings / user ticket mode / contractor mode / isSettingAlertsForHimself
 * @param userRole
 * @param workspaceSettings
 * @param userTicketsMode
 * @param contractorMode
 * @param isSettingAlertsForHimself
 * @return {{email: [] , inApp: []}}
 */
export const getAvailableAlertsBasedOnRole = (userRole, workspaceSettings, userTicketsMode, contractorMode, isSettingAlertsForHimself = false, passageSensorExists) => {
    const emailAlerts = [];
    const inAppAlerts = [];
    const subscriptions = ALERT_OPTIONS(isSettingAlertsForHimself);

    if (isNewSystem()) {
        // TICKETING
        emailAlerts.push(subscriptions.tickets.all);
        inAppAlerts.push(subscriptions.tickets.new)
        // JOBS
        emailAlerts.push(subscriptions.rounds.not_completed);
        inAppAlerts.push(subscriptions.rounds.not_completed);
    } else {
        if (
            userRole === 'Owner' ||
            userRole === 'Admin' ||
            (userRole === 'Contractor' && contractorMode === 'ALL') ||
            (userRole === 'User' && userTicketsMode === 'ALL')
        ) {
            emailAlerts.push(subscriptions.tickets.all);
        }
        // Every role have this alert
        emailAlerts.push(subscriptions.tickets.comment);
        // Owner, Contractors and Admins can see this alert
        if (userRole !== 'User') {
            emailAlerts.push(subscriptions.tickets.assignated);
        }
    }

    /* ROUNDS Alerts for EMAIL */
    // Owner, Admins and Users with the calendar ws can see this alert
    // these alerts are not available if there is no passage capture in the workspace
    if (requiredRoleToSeeRoundView(userRole, workspaceSettings) && passageSensorExists) { // Round alerts
        emailAlerts.push(subscriptions.rounds.all, subscriptions.rounds.comment);
    }

    return {
        email: emailAlerts,
        inApp: inAppAlerts
    };
};
